import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import type { WorkbookRowKind, WorkbookSheet } from "./excel";
import type { ReportWorkbookKind } from "./reportExcel";
import references from "./reportReferenceTheme.json";
const widthNumber = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

const escapeXml = (s: string) => s.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
const decodeXml = (s: string) => s.replaceAll("&quot;", '"').replaceAll("&apos;", "'").replaceAll("&lt;", "<").replaceAll("&gt;", ">").replaceAll("&amp;", "&");
const columnName = (n: number) => String.fromCharCode(64 + n);
const columnNumber = (s: string) => [...s].reduce((n, c) => n * 26 + c.charCodeAt(0) - 64, 0);
const cells = (s: string) => [...s.matchAll(/<c\b[^>]*?\/>|<c\b[^>]*>[\s\S]*?<\/c>/g)].map(m => m[0]);
const rowKind = (sheet: WorkbookSheet, index: number): WorkbookRowKind => sheet.rowKinds?.[index] ?? (index === 0 ? "header" : "detail");

/** Presentation-only projection of already verified values and formula plans.
 * The campaign scope column is omitted in the two reference views, and the
 * general total moves below the daily rows. References follow their logical
 * cells; no source, measure, receipt, return or aggregation rule is changed.
 */
export function applyReportReferencePresentation(bytes: Uint8Array, sheets: WorkbookSheet[], report: ReportWorkbookKind) {
  const files = unzipSync(bytes), theme = references[report];
  let styles = theme.styles;
  const block = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(styles)!;
  const xfs = [...block[1].matchAll(/<xf\b[^>]*?\/>|<xf\b[^>]*>[\s\S]*?<\/xf>/g)].map(m => m[0]);
  const bordersBlock = /<borders\b[^>]*>([\s\S]*?)<\/borders>/.exec(styles)!;
  const borders = [...bordersBlock[1].matchAll(/<border\b[^>]*>[\s\S]*?<\/border>/g)].map(m => m[0]);
  const variants = new Map<string, number>();
  function headingStyle(id: number, center: boolean, remove: string[]) {
    const key = `${id}:${center}:${remove.join(",")}`;
    const found = variants.get(key); if (found !== undefined) return found;
    let xf = xfs[id];
    if (remove.length) {
      let border = borders[Number(/borderId="(\d+)"/.exec(xf)![1])];
      for (const edge of remove) border = border.replace(new RegExp(`<${edge}\\b[^>]*(?:/>|>[\\s\\S]*?</${edge}>)`), `<${edge}/>`);
      let borderId = borders.indexOf(border);
      if (borderId < 0) { borderId = borders.length; borders.push(border); }
      xf = xf.replace(/borderId="\d+"/, `borderId="${borderId}"`);
    }
    if (center) {
      xf = xf.replace(/<alignment\b[^>]*\/>/, "");
      if (xf.endsWith("/>")) xf = xf.slice(0, -2) + "></xf>";
      xf = xf.replace('</xf>', '<alignment horizontal="centerContinuous" vertical="center" wrapText="0"/></xf>');
      xf = xf.replace(/\sapplyAlignment="[^"]*"/, "").replace('<xf ', '<xf applyAlignment="1" ');
    }
    const next = xfs.length; xfs.push(xf); variants.set(key, next); return next;
  }
  const projected = (index: number) => report === "campaign" && (index === 0 || index === 2);
  const rowMap = (index: number, r: number) => report === "campaign" && index === 0 && r >= 3
    ? r === 3 ? sheets[0].rows.length + 1 : r - 1 : r;
  const colMap = (index: number, c: number) => projected(index) && c > 2 ? c - 1 : c;
  function address(index: number, c: string, r: string) {
    return `${c.startsWith("$") ? "$" : ""}${columnName(colMap(index, columnNumber(c.replace("$", ""))))}${r.startsWith("$") ? "$" : ""}${rowMap(index, Number(r.replace("$", "")))}`;
  }
  function formula(source: string, owner: number) {
    return source.replace(/(?:(?:'((?:[^']|'')+)'|([\p{L}_][\p{L}\p{N}_ ]*))!)?(\$?[A-Z]{1,3})(\$?\d+)/gu, (match, quoted: string | undefined, plain: string | undefined, c: string, r: string) => {
      const name = quoted?.replaceAll("''", "'") ?? plain;
      const index = name === undefined ? owner : sheets.findIndex(s => s.name === name);
      if (index < 0) return match;
      const prefix = name === undefined ? "" : match.slice(0, match.lastIndexOf("!") + 1);
      return prefix + address(index, c, r);
    });
  }
  const printNames: string[] = [];
  sheets.forEach((sheet, index) => {
    const ref = theme.sheets[index], info = sheet.theme === "info";
    const file = `xl/worksheets/sheet${index + 1}.xml`;
    let source = strFromU8(files[file]);
    const count = info ? 2 : ref.header.length;
    const last = info ? sheet.rows.length : sheet.rows.length + (report === "campaign" ? 1 : 0);
    const outRows: { r: number; xml: string; kind: WorkbookRowKind }[] = [];
    if (info) {
      sheet.rows.forEach((values, i) => {
        const r = i + 1, ids = i === 0 ? ref.header : ref.detail;
        outRows.push({ r, kind: i === 0 ? "header" : "detail", xml: `<row r="${r}" ht="17" customHeight="1">${values.map((v, c) => `<c r="${columnName(c + 1)}${r}" s="${ids[c]}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(String(v ?? ""))}</t></is></c>`).join("")}</row>` });
      });
    } else {
      for (const row of source.matchAll(/<row\b[^>]*\br="(\d+)"[^>]*>[\s\S]*?<\/row>/g)) {
        const oldR = Number(row[1]), r = rowMap(index, oldR);
        const header = oldR <= (report === "campaign" ? 2 : 1);
        const kind = header ? "header" : rowKind(sheet, oldR - (report === "campaign" ? 2 : 1));
        const ids = header ? oldR === 2 ? ref.subheader : ref.header : ref[kind as "detail" | "subtotal" | "total"];
        const out: string[] = [];
        for (let cell of cells(row[0])) {
          const a = /\br="([A-Z]+)(\d+)"/.exec(cell)!;
          const oldC = columnNumber(a[1]);
          if (projected(index) && oldC === 2) continue;
          const c = colMap(index, oldC), target = `${columnName(c)}${r}`;
          let id = ids[c - 1];
          if (header && report === "campaign") {
            const leading = projected(index) ? 1 : 2;
            if (c <= leading) id = headingStyle(id, false, [oldR === 1 ? "bottom" : "top"]);
            else if (oldR === 1) {
              const position = (c - leading - 1) % 3;
              id = headingStyle(id, true, [...(position > 0 ? ["left"] : []), ...(position < 2 ? ["right"] : [])]);
            }
          }
          cell = cell.replace(/\br="[A-Z]+\d+"/, `r="${target}"`).replace(/\bs="\d+"/, `s="${id}"`);
          cell = cell.replace(/<f>([\s\S]*?)<\/f>/, (_, f: string) => `<f>${escapeXml(formula(decodeXml(f), index))}</f>`);
          // True empty header cells let native Center Across Selection span the
          // group without merged cells or invisible empty-string blockers.
          if (header && /<t(?:\s[^>]*)?><\/t>/.test(cell)) cell = `<c r="${target}" s="${id}"/>`;
          out.push(cell);
        }
        outRows.push({ r, kind, xml: `<row r="${r}" ht="17" customHeight="1">${out.join("")}</row>` });
      }
    }
    outRows.sort((a, b) => a.r - b.r);
    source = source.replace(/<sheetData>[\s\S]*?<\/sheetData>/, `<sheetData>${outRows.map(r => r.xml).join("")}</sheetData>`)
      .replace(/<mergeCells\b[^>]*>[\s\S]*?<\/mergeCells>/g, "")
      .replace(/<conditionalFormatting\b[^>]*>[\s\S]*?<\/conditionalFormatting>/g, "")
      .replace(/<autoFilter\b[^>]*(?:\/>|>[\s\S]*?<\/autoFilter>)/g, "")
      .replace(/<dimension\b[^>]*\/>/, `<dimension ref="A1:${columnName(count)}${last}"/>`);
    const columns = ref.widths.map((width, c) => {
      const inputC = projected(index) && c >= 1 ? c + 1 : c;
      const textLength = Math.max(0, ...sheet.rows.slice(1).map(row => typeof row[inputC] === "string" ? String(row[inputC]).length : 0));
      const valueWidth = Math.max(0, ...sheet.rows.slice(1).map(row => typeof row[inputC] === "number" ? widthNumber.format(Number(row[inputC])).length * .8 + 2 : 0));
      const sales = report === "store-performance" && (index === 0 ? c === 3 : (index === 1 || index === 2) && [2, 3].includes(c));
      const fitted = Math.min(255, Math.max(width, sales ? 22 : 0, info ? 0 : textLength * .8 + 2, info ? 0 : valueWidth));
      return `<col min="${c + 1}" max="${c + 1}" width="${fitted.toFixed(3)}" customWidth="1"/>`;
    }).join("");
    source = source.replace(/<cols>[\s\S]*?<\/cols>/, `<cols>${columns}</cols>`);
    const detailRows = outRows.filter(r => r.kind === "detail").map(r => r.r);
    const detailRefs = (c: string) => {
      const blocks: string[] = [];
      for (let i = 0; i < detailRows.length; i++) {
        const first = detailRows[i]; let end = first;
        while (detailRows[i + 1] === end + 1) end = detailRows[++i];
        blocks.push(first === end ? `${c}${first}` : `${c}${first}:${c}${end}`);
      }
      return blocks.join(" ");
    };
    let rules = "", priority = 1;
    if (!info && report === "store-performance" && index > 0) {
      for (const c of index === 1 ? ["E", "H", "K", "N", "Q", "T"] : ["E", "H"]) {
        if (detailRows.length) rules += `<conditionalFormatting sqref="${detailRefs(c)}"><cfRule type="colorScale" priority="${priority++}"><colorScale><cfvo type="num" val="-0.2"/><cfvo type="num" val="0"/><cfvo type="num" val="0.2"/><color rgb="FFFF0000"/><color rgb="FFFFFFFF"/><color rgb="FF92D050"/></colorScale></cfRule></conditionalFormatting>`;
      }
    }
    if (report === "campaign" && index === 3) {
      let first = 3;
      for (const row of outRows) if (row.kind === "subtotal") {
        const brand = String(sheet.rows[first - 2]?.[0] ?? "");
        const palette = references.campaign.conditionalStyles as Record<string, Record<string, number>>;
        for (const c of ["E", "H", "K"]) if (row.r > first) rules += `<conditionalFormatting sqref="${c}${first}:${c}${row.r - 1}"><cfRule type="expression" dxfId="${palette[brand]?.[c] ?? 0}" priority="${priority++}"><formula>AND(ISNUMBER(${c}${first}),ISNUMBER(${c}$${row.r}),${c}${first}&lt;${c}$${row.r})</formula></cfRule></conditionalFormatting>`;
        first = row.r + 1;
      }
    }
    const filter = !info && ref.filter ? `<autoFilter ref="A1:${columnName(count)}${last}"/>` : "";
    source = source.replace('</sheetData>', `</sheetData>${filter}${rules}`);
    if (ref.tabColor.rgb) source = source.replace(/<tabColor\b[^>]*\/>/, `<tabColor rgb="${ref.tabColor.rgb}"/>`);
    const name = escapeXml(`'${sheet.name.replaceAll("'", "''")}'`);
    printNames.push(`<definedName name="_xlnm.Print_Titles" localSheetId="${index}">${name}!$1:$${report === "campaign" && !info ? 2 : 1}</definedName>`, `<definedName name="_xlnm.Print_Area" localSheetId="${index}">${name}!$A$1:$${columnName(count)}$${last}</definedName>`);
    files[file] = strToU8(source);
  });
  styles = styles.replace(block[0], `<cellXfs count="${xfs.length}">${xfs.join("")}</cellXfs>`)
    .replace(bordersBlock[0], `<borders count="${borders.length}">${borders.join("")}</borders>`);
  files["xl/styles.xml"] = strToU8(styles);
  files["xl/theme/theme1.xml"] = strToU8(theme.theme);
  let rels = strFromU8(files["xl/_rels/workbook.xml.rels"]);
  if (!rels.includes('/theme"')) rels = rels.replace('</Relationships>', '<Relationship Id="reportReferenceTheme" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/theme" Target="theme/theme1.xml"/></Relationships>');
  files["xl/_rels/workbook.xml.rels"] = strToU8(rels);
  let content = strFromU8(files["[Content_Types].xml"]);
  if (!content.includes('/xl/theme/theme1.xml')) content = content.replace('</Types>', '<Override PartName="/xl/theme/theme1.xml" ContentType="application/vnd.openxmlformats-officedocument.theme+xml"/></Types>');
  files["[Content_Types].xml"] = strToU8(content);
  let workbook = strFromU8(files["xl/workbook.xml"]);
  workbook = /<definedNames>/.test(workbook) ? workbook.replace(/<definedNames>[\s\S]*?<\/definedNames>/, `<definedNames>${printNames.join("")}</definedNames>`) : workbook.replace('</sheets>', `</sheets><definedNames>${printNames.join("")}</definedNames>`);
  files["xl/workbook.xml"] = strToU8(workbook);
  return zipSync(files, { level: 6 });
}
